import { useEffect, useState, type MouseEvent } from 'react';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Flex from '@/components/common/Flex';
import { Link, useLocation, useSearchParams } from 'react-router-dom';
import SubtleBadge from '@/components/common/SubtleBadge';
import { FiArrowDownCircle, FiArrowUpCircle, FiCircle } from 'react-icons/fi';
import { getColor } from '@/helpers/utils';
import { AiOutlineZoomIn } from 'react-icons/ai';
import countSuffix from '@/helpers/countSuffix';
import AgGridWrapper from '@/app/common/components/ag-grid/AgGridWrapper';
import type { AgGridColumn } from '@/app/common/components/ag-grid/types';
import { useDispatch } from 'react-redux';
import { useAppContext } from '@/Main';
import { showDrawer } from '@/app/common/reducers/drawer';
import type {
  ChecklistCellRendererParams,
  ChecklistConfigWithCount,
  ChecklistItemData
} from './checklistTypes';

interface CheckListTableProps {
  checkListConfigWithCount: ChecklistConfigWithCount;
  searchText: string;
}

const CheckListTable = ({
  checkListConfigWithCount,
  searchText
}: CheckListTableProps): React.JSX.Element => {
  const all = [
    ...checkListConfigWithCount.errors,
    ...checkListConfigWithCount.warning,
    ...checkListConfigWithCount.info
  ];
  const [checkListConfig, setCheckListConfig] =
    useState<ChecklistItemData[]>(all);
  // فیلتر بر اساس سرج
  const handleSearch = (text: string): void => {
    const normalizedText = text.toLowerCase();
    const mode = convertModeToEn(normalizedText);
    const priority = convertPriorityToEn(normalizedText);
    const filteredIssues = all.filter(
      item =>
        item.show_title?.toLowerCase().includes(normalizedText) ||
        item.gp?.toLowerCase().includes(normalizedText) ||
        (mode && item.mode?.toLowerCase().includes(mode)) ||
        (priority && item.priority?.toLowerCase().includes(priority))
    );
    setCheckListConfig(filteredIssues);
  };
  useEffect(() => handleSearch(searchText), [searchText]);

  const convertModeToEn = (text: string): string | undefined => {
    switch (text) {
      case 'خطا':
        return 'error';
      case 'هشدار':
        return 'warning';
      case 'اطلاعیه':
        return 'info';
    }
  };
  const convertPriorityToEn = (text: string): string | undefined => {
    switch (text) {
      case 'بالا':
        return 'high';
      case 'متوسط':
        return 'medium';
      case 'پایین':
        return 'low';
    }
  };
  return (
    <div className="position-relative">
      <AgGridWrapper
        perPage={150}
        settings={{ pagination: false }}
        height="700px"
        data={checkListConfig}
        columns={tableCol}
      />
    </div>
  );
};

export default CheckListTable;

const tableCol: AgGridColumn[] = [
  {
    field: 'show_title',
    title: 'نام مورد',
    suppressMovable: true,
    suppressHeaderMenuButton: true,
    minWidth: 236,
    flex: 1,
    tooltip: null,
    cellRenderer: (params: ChecklistCellRendererParams) => {
      const [searchParams] = useSearchParams();
      const { pathname } = useLocation();
      const dispatch = useDispatch();
      const {
        config: { isRTL }
      } = useAppContext();
      return (
        <Flex
          alignItems="baseline"
          justifyContent="start"
          className="fs--1 check-list-item gap-0 text-700"
        >
          <Link
            className="text-decoration-none text-reset hover-800"
            to={`${pathname}?issues=${params.data.link}`}
            state={{ path: `${pathname}`, query: searchParams.get('issues') }}
            // className={params.data.count === 0 ? 'disabled-link' : ''}
          >
            <Flex
              alignItems="baseline"
              justifyContent="between"
              className="fs--1 check-list-item gap-2"
            >
              <p
                className={`mb-1 ${
                  params.data.link === searchParams.get('issues')
                    ? 'text-primary fw-bold'
                    : 'text-700'
                }`}
              >
                {params.data.show_title}
              </p>
            </Flex>
          </Link>
          <FontAwesomeIcon
            icon={['far', 'question-circle']}
            transform="shrink-1"
            style={{ transform: isRTL ? 'scaleX(-1)' : 'scaleX(1)' }}
            className={`ms-1 text-400 cursor-pointer check-list-item`}
            id="weeklySalesTooltip"
            onClick={(event: MouseEvent<SVGSVGElement>) => {
              event.stopPropagation();
              event.preventDefault();

              dispatch(
                showDrawer({
                  key: 'help',
                  payload: {
                    slug: params.data.link,
                    title: params.data.show_title,
                    tooltip: params.data.tooltip
                  }
                })
              );
            }}
          />
        </Flex>
      );
    }
  },
  {
    field: 'count',
    width: 80,
    suppressHeaderMenuButton: true,
    title: 'تعداد',
    type: 'number',
    cellRenderer: (params: ChecklistCellRendererParams) => (
      <Flex
        alignItems="center"
        justifyContent="center"
        className="position-relative fs-0 h-100 py-1"
      >
        {countSuffix(params.data.count)}
      </Flex>
    )
  },
  {
    field: 'mode',
    suppressHeaderMenuButton: true,
    title: 'نوع مورد',
    width: 150,
    cellRenderer: (params: ChecklistCellRendererParams) => {
      const { mode } = params.data;
      return (
        <Flex
          alignItems="center"
          justifyContent="center"
          className="position-relative fs-0 h-100 py-1"
        >
          {mode === 'error' ? (
            <SubtleBadge bg="danger" className="me-2">
              خطا
            </SubtleBadge>
          ) : mode === 'warning' ? (
            <SubtleBadge bg="warning" className="me-2">
              هشدار
            </SubtleBadge>
          ) : (
            <SubtleBadge bg="info" className="me-2">
              اطلاعیه
            </SubtleBadge>
          )}
        </Flex>
      );
    }
  },
  {
    field: 'priority',
    title: 'اولویت',
    width: 150,
    suppressHeaderMenuButton: true,
    cellRenderer: (params: ChecklistCellRendererParams) => {
      const { priority } = params.data;
      return (
        <Flex
          alignItems="center"
          justifyContent="start gap-2"
          className="position-relative h-100 py-1"
        >
          {priority == 'high' ? (
            <>
              <FiArrowUpCircle title="اولویت بالا" color={getColor('danger')} />
              بالا
            </>
          ) : priority == 'medium' ? (
            <>
              <FiCircle title="اولویت متوسط" color={getColor('warning')} />
              متوسط
            </>
          ) : (
            <>
              <FiArrowDownCircle
                title="اولویت پایین"
                color={getColor('info')}
              />
              پایین
            </>
          )}
        </Flex>
      );
    }
  },
  {
    field: 'gp',
    width: 100,
    suppressHeaderMenuButton: true,
    title: 'دسته',
    sort: 'desc'
  },
  {
    title: ' ',
    width: 100,
    field: ' ',
    cellRenderer: (params: ChecklistCellRendererParams) => {
      const { link } = params.data;
      const [searchParams] = useSearchParams();
      const { pathname } = useLocation();
      return (
        <Link
          className="text-decoration-none text-reset hover-800"
          to={`${pathname}?issues=${link}`}
          state={{ path: `${pathname}`, query: searchParams.get('issues') }}
          // className={params.data.count === 0 ? 'disabled-link' : ''}
        >
          <Flex
            alignItems="baseline"
            justifyContent="center"
            className={`fs--1 check-list-item gap-2`}
          >
            <p
              className={`mb-1 ${
                link === searchParams.get('issues')
                  ? 'text-primary fw-bold'
                  : 'text-700'
              }`}
            >
              <AiOutlineZoomIn className="cursor-pointer fs-0" />{' '}
              مشاهده
            </p>
          </Flex>
        </Link>
      );
    }
  }
];
